import { DetectionIcon } from '@vlabs/icons';
import type { TEventReferencePreview } from '@vlabs/monokit/entities/matcher/types/types';
import { EventStreamLink } from '@vlabs/shared/components/stream-link/EventStreamLink';
import { routes } from '@vlabs/shared/navigation/routes';
import { formatDateTime } from '@vlabs/shared/utils/date';
import { FaceSample, Tag, Control } from '@vlabs/uikit';
import { buildLocationString } from '@vlapps/point/src/utils/location';
import { round } from 'lodash';
import { generatePath } from 'react-router-dom';

import { useSearchPageTranslation } from '../../../../i18n';
import { useReferenceMatchData } from '../useReferenceMatchData';
import st from './EventPreview.module.sass';

export interface EventPreviewProps {
  reference: TEventReferencePreview;
}

export function EventPreview({ reference }: EventPreviewProps): JSX.Element {
  const { t } = useSearchPageTranslation();

  const [topMatch, topMatchType] = useReferenceMatchData(reference?.top_match);
  const location = buildLocationString(reference);

  const topMatchSimilarity = typeof (reference?.top_match?.similarity) === 'number' ? round(reference.top_match.similarity * 100, 2) : '-';

  return (
    <div
      className={st.Root}
      data-testid="table.referencesPreview"
    >
      <div
        className={st.TopSection}
        data-testid="eventPreviewTopSection"
      >
        <FaceSample
          className={st.Sample}
          src={reference?.uiFaceDetection?.uiSampleURL}
        />
        <FaceSample
          className={st.Sample}
          // @ts-expect-error требуется типизация хука useMatchData
          src={topMatch?.uiFaceDetection?.uiSampleURL}
        />
        <div className={st.Info}>
          <div className={st.Badges}>
            <Tag
              kind="neutral"
              value="Совпадение"
            />
            <Tag
              className={st.DetectionTag}
              icon={<DetectionIcon />}
              kind="positive"
              value={t('reference_selector.event_preview.top_section.similarity_tag.label', { context: topMatchType, value: topMatchSimilarity })}
            />
          </div>
          <div className={st.Date}>{formatDateTime(reference?.create_time)}</div>
          <EventStreamLink
            kind="secondary"
            placeholders={{
              removed: t('reference_selector.event_preview.videostream.removed'),
              unavailable: t('reference_selector.event_preview.videostream.unavailable'),
            }}
            size="medium"
            streamId={reference?.stream_id}
          />
        </div>
      </div>
      <div className={st.BottomSection}>
        <div className={st.Location}>{location}</div>
        <div>
          <span className={st.Lists}>{t('reference_selector.event_preview.lists')}</span>
          {!!topMatch?.list && (topMatch.lists as { list_id?: string; user_data?: string }[]).map((list) => (
            <Control.Link
              key={list?.list_id}
              kind="secondary"
              // @ts-expect-error требуется типизация routes
              to={generatePath(routes.lists.read, { listId: list?.list_id })}
            >
              {list?.user_data ?? list?.list_id}
            </Control.Link>
          ))}
          {!topMatch?.list && ' - '}
        </div>
      </div>
    </div>
  );
}
